import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import Navigator, { TNavigatorItem } from '@/app/component/atom/navigator'
import Loading from '@/app/component/guard/loading'
import SidebarLayout from '@/app/component/layout/sidebar-layout'
import OrgWarning from '@/app/module/dashboard/component/org-warning'
import { useGetBalance } from '@/app/module/payments/hooks/use-get-balance'
import { useGetCreditCards } from '@/app/module/payments/hooks/use-get-credit-cards'
import { selectUserError, selectUserLoading } from '@/app/module/user/store/ts/selectors'
import { LoadingError } from '@/app/types'

import OverviewTab from './overview'
import PurchaseReceiptsTab from './purchase-receipts'
import SettingsTab from './settings'
import UsageStatementsTab from './usage-statements'

export enum Tabs {
  Overview = 'overview',
  Receipts = 'receipts',
  Invoices = 'invoice',
  Settings = 'settings',
}

type Props = {
  tabName?: string
}

const Billing: React.FC<Props> = ({ tabName = Tabs.Overview }) => {
  const navigate = useNavigate()
  const error = useSelector(selectUserError) as LoadingError
  const loading = useSelector(selectUserLoading)

  const { isFetching: isBalanceLoading } = useGetBalance()
  const { isFetching: isCardsLoading } = useGetCreditCards()

  const isLoading = loading || isBalanceLoading || isCardsLoading

  const handleItemClick = React.useCallback(
    (tab: string) => {
      navigate(`/billing/${tab}`)
    },
    [navigate],
  )

  const sidebar = (
    <Navigator activePath={tabName} id="billing" items={navigatorItems} title="Billing" onItemClick={handleItemClick} />
  )

  const content = (
    <Container id={`billing-content-${tabName}`}>
      <OrgWarning />

      {tabName === Tabs.Overview && <OverviewTab />}
      {tabName === Tabs.Settings && <SettingsTab />}
      {tabName === Tabs.Receipts && <PurchaseReceiptsTab />}
      {tabName === Tabs.Invoices && <UsageStatementsTab />}
    </Container>
  )

  return (
    <Loading isLoading={isLoading} error={error}>
      <Root>
        <SidebarLayout sidebar={sidebar} content={content} />
      </Root>
    </Loading>
  )
}

const navigatorItems: TNavigatorItem<string>[] = [
  {
    icon: 'credit_card',
    label: 'Add Funds',
    path: Tabs.Overview,
  },
  {
    icon: 'settings',
    label: 'Settings',
    path: Tabs.Settings,
  },
  {
    icon: 'receipt',
    label: 'Purchase Receipts',
    path: Tabs.Receipts,
  },
  {
    icon: 'description',
    label: 'Usage Statements',
    path: Tabs.Invoices,
  },
]

const Root = styled('div')({
  paddingBottom: '1px',
  height: '100%',
})

const Container = styled(Box)(() => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}))

export default Billing
